import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useGetTheme } from '../../../modules/theme';
import {
  getColors,
  getDatasetDifferences,
  subtractArrays
} from './stacked-horizontal-bar-chart.function';
import { StackedHorizontalBarChartProps } from './stacked-horizontal-bar-chart.type';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;
const DEFAULT_STEP = 10;

export const StackedHorizontalBarChart = ({
  title,
  labels,
  values,
  backgroundBar,
  diffsBar,
  options
}: StackedHorizontalBarChartProps) => {
  const theme = useGetTheme();
  const colors = getColors(theme);
  const hasBackgroundBar = !!backgroundBar;
  const hasDiffsBar = !!diffsBar;

  const diffs =
    hasBackgroundBar && hasDiffsBar
      ? getDatasetDifferences(values, backgroundBar.values)
      : new Array(values.length).fill(0);

  const chartData: ChartData<'bar'> = {
    labels,
    datasets: [
      {
        label: title,
        data: subtractArrays(values, diffs),
        backgroundColor: colors.primary,
        hoverBackgroundColor: colors.primary,
        stack: 'bar',
        order: 1
      }
    ]
  };

  if (hasBackgroundBar)
    chartData.datasets.push({
      label: backgroundBar.label,
      data: backgroundBar.values,
      backgroundColor: colors.secondary,
      hoverBackgroundColor: colors.secondary,
      stack: 'bar',
      order: 3
    });

  if (hasDiffsBar)
    chartData.datasets.push({
      label: diffsBar.label,
      data: values,
      backgroundColor: colors.error,
      hoverBackgroundColor: colors.error,
      stack: 'bar',
      order: 2
    });

  const chartOptions: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        onClick: () => {
          return;
        },
        position: 'bottom',
        labels: {
          filter: (legendItem) => legendItem.text !== diffsBar?.label,
          font: {
            size: 14,
            family: 'Roboto'
          },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 7,
          boxHeight: 7
        }
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: true,
        backgroundColor: '#464E58',
        usePointStyle: true,
        boxWidth: 7,
        boxHeight: 7,
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label || '';
            const index = context.dataIndex;
            const diffDelta = diffs[index];
            const suffix = options?.tooltipSuffix ?? '';

            if (datasetLabel === diffsBar?.label) {
              if (diffDelta === 0) return '';
              return `${datasetLabel}: ${diffs[index]}${suffix}`;
            }
            if (datasetLabel === title) return `${datasetLabel}: ${values[index]}${suffix}`;
            return `${datasetLabel}: ${context.formattedValue}${suffix}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: false,
        min: options?.min ?? DEFAULT_MIN,
        max: options?.max ?? DEFAULT_MAX,
        ticks: { stepSize: options?.step ?? DEFAULT_STEP },
        border: {
          dash: [4, 4]
        }
      },
      y: {
        stacked: true,
        ticks: {
          font: { size: 14, family: 'Roboto' }
        }
      }
    }
  };

  return <Bar data={chartData} options={chartOptions} />;
};
